import { formatDate } from './utils';

/**
 * Formats a temperature value with °C suffix
 * @param value - The temperature value to format
 * @returns Formatted string or '-' if value is null/undefined/empty
 */
export const formatTemperature = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return `${Number(value).toFixed(1)}°C`;
};

/**
 * Formats a load current value with A suffix
 * @param value - The current value to format
 * @returns Formatted string or '-' if value is null/undefined/empty
 */
export const formatLoadCurrent = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return `${Number(value).toFixed(2)}A`;
};

/**
 * Generic value formatter that handles null, dates, and other types
 * @param value - The value to format
 * @returns Formatted string or '-' if value is null/undefined/empty
 */
export const formatReportValue = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  // Format dates if they look like dates
  if (value instanceof Date || (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/))) {
    return formatDate(value);
  }
  return String(value);
};

/**
 * Converts snake_case string to Title Case
 * @param str - The snake_case string to convert
 * @returns Title Case string
 */
export const snakeCaseToTitleCase = (str: string): string => {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Gets field label from client preferences or defaults
 * @param fieldName - The field name to get label for
 * @param defaultLabels - Object mapping field names to default labels
 * @param clientPreferences - Client preferences object (may contain custom labels)
 * @param preferenceKey - Key in client preferences for custom labels (e.g., 'multiCircuitReportFieldLabels')
 * @returns The label for the field
 */
export const getFieldLabel = (
  fieldName: string,
  defaultLabels: Record<string, string>,
  clientPreferences?: any,
  preferenceKey: string = 'multiCircuitReportFieldLabels'
): string => {
  const customLabels = clientPreferences?.preferences?.[preferenceKey] || {};
  return customLabels[fieldName] || defaultLabels[fieldName] || snakeCaseToTitleCase(fieldName);
};
